import { useEffect, useState } from "react";
import { User, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", title: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        title: user.title || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", {
        name: form.name || undefined,
        title: form.title || undefined,
        phone: form.phone || undefined,
      });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const initials = (form.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
          Account
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Your profile</h1>
        <p className="mt-1 text-sm text-slate-500">
          Update how you appear to signers and teammates.
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-4 pb-6 border-b border-slate-100">
          <div className="h-16 w-16 rounded-full bg-slate-900 text-white text-xl font-semibold flex items-center justify-center">
            {initials}
          </div>
          <div>
            <div className="text-base font-semibold">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
            <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-medium capitalize">
              <User className="h-3 w-3" />
              {user?.role?.replace(/_/g, " ")}
            </div>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-5">
          <div>
            <Label>Full name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 h-11"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label>Job title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 h-11"
              data-testid="profile-title-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1.5 h-11"
              data-testid="profile-phone-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 h-11" />
            <div className="mt-1 text-xs text-slate-500">
              Contact support to change your email address.
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="profile-save-btn"
          >
            <Save className="h-4 w-4" />
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
